import { rgba } from 'polished';
import { isNumber } from 'shared/utils/is-number';
import styled, { css, DefaultTheme } from 'styled-components';

import { Colors } from './types';

const predefinedColors = Object.values(Colors);

const getColor = (color: Colors | string, theme: DefaultTheme) => {
  if (!predefinedColors.includes(color as Colors)) {
    return {
      default: color,
      background: rgba(color, 0.4),
      border: color,
    };
  }

  switch (color) {
    case Colors.Info:
      return {
        default: theme.palette.info.main,
        background: rgba(theme.palette.info.main, 0.4),
        border: theme.palette.info.main,
      };
    case Colors.Warning:
      return {
        default: theme.palette.warning.main,
        background: rgba(theme.palette.warning.main, 0.4),
        border: theme.palette.warning.main,
      };
    case Colors.Error:
      return {
        default: theme.palette.error.main,
        background: rgba(theme.palette.error.main, 0.4),
        border: theme.palette.error.main,
      };
    case Colors.Success:
      return {
        default: theme.palette.success.main,
        background: rgba(theme.palette.success.main, 0.4),
        border: theme.palette.success.main,
      };
    default:
      return {
        default: theme.palette.grey[400],
        background: theme.palette.grey[400],
        border: theme.palette.primary.main,
      };
  }
};

// The checkbox's border color, exposed so siblings rendered on the same surface
// (the calendar card's sync badge) can match it instead of re-deriving the map.
// Mirrors Checkbox's own `color = Colors.Default` fallback.
export const getCheckboxBorderColor = (
  color: Colors | string | undefined,
  theme: DefaultTheme,
): string => getColor(color ?? Colors.Default, theme).border;

export const Container = styled.label`
  line-height: 62.5%;
  display: inline-block;
  cursor: pointer;
`;

export const Checkbox = styled.span<{
  $color: Colors | string;
  $isPlaceholder: boolean;
  $isRecommended: boolean;
  $isDisabled: boolean;
  $completionPercentage?: number;
  $isSmall: boolean;
  $isFaded?: boolean;
}>`
  pointer-events: none;
  position: relative;
  display: inline-block;
  width: ${({ theme, $isSmall }) =>
    theme.isMobile ? '1.8rem' : $isSmall ? '1.2rem' : '1.4rem'};
  height: ${({ theme, $isSmall }) =>
    theme.isMobile ? '1.8rem' : $isSmall ? '1.2rem' : '1.4rem'};
  margin: ${({ theme }) => theme.spacing(theme.isMobile ? 1.75 : 0.75)};
  background-color: transparent;

  border-radius: ${({ theme, $isSmall }) =>
    theme.spacing(theme.isMobile ? 1.75 : $isSmall ? 1 : 1.25)};

  transition: all 200ms;

  ${({ theme, $color, $completionPercentage }) =>
    isNumber($completionPercentage)
      ? css`
          @property --startDeg {
            syntax: '<angle>';
            inherits: false;
            initial-value: 0deg;
          }
          @property --startDegOne {
            syntax: '<angle>';
            inherits: false;
            initial-value: 0deg;
          }
          @property --endDeg {
            syntax: '<angle>';
            inherits: false;
            initial-value: 0deg;
          }

          --startDeg: ${3.6 * $completionPercentage!}deg;
          --startDegOne: ${3.6 * $completionPercentage! + 0.0001}deg;
          --endDeg: ${360 - 0.0001}deg;

          border: 0.15rem solid #0000;
          transition:
            --startDeg 500ms,
            --startDegOne 500ms,
            --endDeg 500ms;
          background: ${() => {
              const { background, border } = getColor($color, theme);

              if (!isNumber($completionPercentage)) {
                return border;
              }

              return `conic-gradient(
    from 0deg at 50% 50%,
    ${border} 0deg,
    ${border} var(--startDeg),
    ${background} var(--startDegOne),
    ${background} var(--endDeg),
    ${background} 360deg
  )`;
            }}
            border-box;
          border-image-slice: 1;

          /* Clip the conic-gradient to the border ring only, leaving the
             center transparent (an opaque inner layer can't do this). */
          -webkit-mask:
            linear-gradient(#fff 0 0) padding-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
        `
      : css`
          border: 0.15rem solid ${getColor($color, theme).default};
        `};

  ${({ $isPlaceholder }) =>
    $isPlaceholder &&
    css`
      border-style: dotted;
    `}

  ${({ $isRecommended, theme }) =>
    $isRecommended &&
    css`
      border-color: ${theme.palette.grey[300]};
    `};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      opacity: 0.4;
      border-color: ${({ theme }) => theme.palette.grey[400]};
    `};

  ${({ $isFaded }) =>
    $isFaded &&
    css`
      opacity: 0.5;
    `};

  :hover {
    background-color: ${({ theme }) => theme.palette.grey[100]};
  }
`;

export const Checkmark = styled.span<{ $color?: string; $isSmall: boolean }>`
  pointer-events: none;
  opacity: 0;
  position: absolute;
  top: 50%;
  left: 50%;

  transform: translate(-50%, -50%);
  transition: opacity 200ms;

  font-size: ${({ $isSmall }) => ($isSmall ? '0.8rem' : '1rem')};
  line-height: 62.5%;
  color: ${({ theme }) => theme.palette.primary.contrastText};
`;

export const Input = styled.input<{
  $predefinedColors?: string;
  $color: Colors | string;
  $keepColorOnChecked: boolean;
  $isFaded?: boolean;
}>`
  position: absolute;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;

  border: none;
  visibility: hidden;

  &:checked {
    + ${Checkbox} {
      background: ${({
        theme,
        $keepColorOnChecked,
        $color,
        $predefinedColors,
      }) =>
        ($keepColorOnChecked ? getColor($color, theme) : undefined) ||
        $predefinedColors ||
        theme.palette.primary.dark};
      border-color: transparent;

      /* The progress variant masks itself to a ring; once checked we want the
         full solid fill + checkmark, so drop that mask. */
      -webkit-mask: none;
      mask: none;

      ${Checkmark} {
        opacity: 1;
      }

      ${({ $isFaded, $predefinedColors, theme }) =>
        $isFaded &&
        css`
          opacity: 1;
          background: ${rgba(
            $predefinedColors || theme.palette.primary.dark,
            0.5,
          )};
        `}
    }
  }
`;

export const Label = styled.span`
  display: inline-block;
  margin-left: ${({ theme }) => theme.spacing(2)};
`;
